import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';

export default function HowItWorksPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const { isLoggedIn, logout, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => {
    if (isAdminLoggedIn) return 'Admin Panel';
    return user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    logout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  const steps = [
    {
      emoji: '🔍',
      title: 'Find & Choose Your Home',
      tag: 'Step 01',
      description: `Browse our curated catalogue of fully verified homes across Ibadan. Use filters to narrow by location, budget, and bedroom count. Every listing on LiteRent has been physically inspected and legally verified before it goes live — so you can search with confidence.`,
      highlight: 'All listings are physically inspected before going live.',
    },
    {
      emoji: '✅',
      title: 'Get Verified and Onboarded',
      tag: 'Step 02',
      description: `To protect every tenant and landlord on our platform, LiteRent conducts a thorough verification before any application is approved. You will be asked to submit your personal details, a valid means of identification, and proof of employment or income — such as a pay slip, recent bank statements, or business records. Our team will then carry out a physical verification visit to confirm that your information is accurate and that you are genuinely employed or actively earning. A one-time verification fee applies: ₦10,000 for annual rents below ₦1,000,000, and ₦20,000 for rents of ₦1,000,000 and above. This fee enables us to conduct the process promptly and professionally. Once verified, you will receive your welcome pack, signed agreement documents, and full onboarding details by email — typically within 3 to 7 business days.`,
      highlight: 'A physical verification visit is conducted to confirm your employment or income.',
    },
    {
      emoji: '💰',
      title: 'Pay Just 30% to Move In',
      tag: 'Step 03',
      description: `Instead of paying a full year of rent upfront, LiteRent only requires 30% of your annual rent to move in. The remaining 70% is spread over 11 affordable monthly installments with a transparent 4.8% monthly interest rate. From your second year onwards, payments are spread over 12 months at 4.4% monthly interest.`,
      highlight: 'Move in with 30% upfront. Pay the rest monthly.',
    },
    {
      emoji: '🏡',
      title: 'Move In & Enjoy Your Home',
      tag: 'Step 04',
      description: `Once your upfront payment and verification fee are confirmed, your keys are handed over and your lease agreement is activated. LiteRent stays with you throughout your tenancy — from maintenance referrals to lease renewals. You are never on your own.`,
      highlight: 'Full support from us throughout your stay.',
    },
  ];

  const faqs = [
    {
      q: 'What is the verification fee?',
      a: `The verification fee is ₦10,000 for annual rents below ₦1,000,000, and ₦20,000 for rents of ₦1,000,000 and above. This is a one-time fee paid at the start to cover the cost of your physical verification visit and swift onboarding.`,
    },
    {
      q: 'Why does LiteRent conduct a physical verification?',
      a: `We carry out a physical verification visit to confirm that the information you have provided — including your place of work or source of income — is accurate and genuine. This step protects both you and the landlord, and ensures that our platform remains trustworthy for everyone on it.`,
    },
    {
      q: 'What happens if I miss a monthly payment?',
      a: `Late payments are handled according to the terms in your signed agreement. Please contact us as soon as possible if you anticipate difficulty making a payment — we aim to work constructively with tenants wherever possible.`,
    },
    {
      q: 'How do I renew my lease?',
      a: `Lease renewals are processed through your tenant dashboard. A ₦7,000 renewal fee applies, and your subsequent year's rent is spread over 12 monthly installments at a 4.4% monthly interest rate.`,
    },
    {
      q: 'Are all listed homes physically verified?',
      a: `Yes. Every home on LiteRent is inspected by our team before it is listed. We verify ownership, structural condition, and the legal status of the property. If it is on LiteRent, it is real.`,
    },
    {
      q: 'Can I apply if I am self-employed?',
      a: `Yes. We welcome applications from self-employed individuals. You will need to provide recent bank statements and a brief description of your business or income source. Our team reviews each application on its own merits, including during the physical verification visit.`,
    },
    {
      q: 'What cities does LiteRent currently operate in?',
      a: `LiteRent currently operates exclusively in Ibadan, Oyo State. We have deliberately chosen to go deep in one city before expanding — so we can provide the best possible service to every tenant and landlord we work with.`,
    },
  ];

  return (
    <>
      <SEOHead title="How LiteRent Works | 30% Upfront Rent in Ibadan" description="See how LiteRent helps eligible renters in Ibadan move into verified homes with 30% upfront and spread the balance over time." canonical="https://literent.com.ng/how-it-works" />
    <main className="min-h-screen bg-[#fdfdfd] text-[#1a1a1a] overflow-x-hidden flex flex-col">

      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body { overflow-x: hidden; max-width: 100vw; }

        .step-description {
          font-size: clamp(0.875rem, 2.2vw, 1rem);
          line-height: 1.75;
          color: rgba(26,26,26,0.70);
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .faq-answer {
          font-size: clamp(0.875rem, 2.2vw, 1rem);
          line-height: 1.75;
          color: rgba(26,26,26,0.70);
        }
        .safe-wrap { word-break: break-word; overflow-wrap: anywhere; }

        /* Hide connector on very small phones */
        @media (max-width: 400px) {
          .step-connector { display: none; }
        }
        /* Stats strip single column on tiny screens */
        @media (max-width: 320px) {
          .stats-grid { grid-template-columns: 1fr !important; }
        }
        /* Full-width CTA buttons on mobile */
        @media (max-width: 639px) {
          .cta-btn { width: 100%; justify-content: center; }
        }
      `}</style>

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">

            <Link to="/" className="flex-shrink-0">
              <img
                src="/Logo.jpg"
                alt="LiteRent Logo"
                className="h-10 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }}
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {[
                { to: '/', label: 'Home' },
                { to: '/how-it-works', label: 'How It Works', active: true },
                { to: '/browse', label: 'Browse Houses' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-medium transition-colors text-sm xl:text-base ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79] hover:text-[#1f3a5c]'}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center space-x-3">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`px-4 xl:px-6 py-2 xl:py-2.5 text-white rounded-full font-medium transition shadow-sm text-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="px-4 xl:px-6 py-2 xl:py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition text-sm">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="px-4 xl:px-6 py-2 xl:py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition text-sm">Login</button></Link>
                    <Link to="/signup"><button className="px-4 xl:px-6 py-2 xl:py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition text-sm">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>

            {/* Hamburger — min touch target 44×44 */}
            <button
              className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#294c79]/20"
              style={{ minWidth: 44, minHeight: 44 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <svg className="h-6 w-6 sm:h-7 sm:w-7 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-4 px-4">
            <div className="flex flex-col">
              {[
                { to: '/', label: 'Home' },
                { to: '/how-it-works', label: 'How It Works', active: true },
                { to: '/browse', label: 'Browse Houses' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`py-3 px-2 text-base font-medium border-b border-gray-50 last:border-0 ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79]'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 mt-2 flex flex-col space-y-3">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`w-full py-3.5 text-white rounded-full font-medium ${isAdminLoggedIn ? 'bg-red-600' : 'bg-[#294c79]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full py-3.5 border-2 border-red-500 text-red-600 rounded-full font-medium">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="w-full py-3.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="w-full py-3.5 bg-[#a5c250] text-white rounded-full font-medium">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ══ HERO ══ */}
      <section className="bg-[#294c79] relative overflow-hidden py-12 sm:py-16 md:py-24 px-4 sm:px-8">
        <div className="absolute -top-24 -right-24 w-64 sm:w-96 h-64 sm:h-96 bg-[#a5c250]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 sm:w-64 h-48 sm:h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10 px-2">
          <span className="inline-block bg-[#a5c250]/20 border border-[#a5c250]/30 text-[#c8e87a] text-[10px] sm:text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full tracking-widest uppercase mb-4 sm:mb-6">
            Simple. Transparent. For You.
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-5">
            How LiteRent Works
          </h1>
          <p className="text-white/75 text-sm sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            From browsing to getting your keys — in four clear steps. No surprises, no hidden costs, no full year of rent demanded upfront.
          </p>

          {/* Stats strip */}
          <div className="mt-8 sm:mt-12 grid grid-cols-3 stats-grid gap-3 sm:gap-4 max-w-xs sm:max-w-xl mx-auto">
            {[
              { num: '30%', label: 'to move in' },
              { num: '11', label: 'monthly payments' },
              { num: '100%', label: 'verified homes' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 border border-white/15 rounded-xl sm:rounded-2xl py-3 sm:py-4 px-2 sm:px-3">
                <p className="text-lg sm:text-2xl md:text-3xl font-bold text-[#a5c250] leading-tight">{s.num}</p>
                <p className="text-white/60 text-[9px] sm:text-xs mt-0.5 sm:mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ STEPS ══ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-5 sm:space-y-8">
          {steps.map((step, i) => (
            <div key={i} className="group flex gap-3 sm:gap-6 md:gap-8 items-start">

              {/* Step number + connector */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-[#294c79] text-white flex items-center justify-center text-xs sm:text-xl md:text-2xl font-bold shadow-md group-hover:bg-[#a5c250] transition-colors duration-300 select-none">
                  {String(i + 1).padStart(2, '0')}
                </div>
                {i < steps.length - 1 && (
                  <div className="step-connector w-0.5 flex-1 min-h-[2rem] sm:min-h-[3rem] bg-[#e8f2d1] mt-2 sm:mt-3" />
                )}
              </div>

              {/* Card */}
              <div className="flex-1 min-w-0 bg-white rounded-2xl sm:rounded-3xl border border-[#e8f2d1] p-4 sm:p-7 md:p-10 shadow-sm group-hover:shadow-lg group-hover:border-[#a5c250]/40 transition-all duration-300 mb-2 sm:mb-3">
                <div className="flex items-start gap-3 sm:gap-4 md:gap-5">
                  <span className="text-2xl sm:text-4xl md:text-5xl flex-shrink-0 leading-none mt-0.5">{step.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block text-[#a5c250] text-[9px] sm:text-xs font-bold tracking-widest uppercase mb-1 sm:mb-2">{step.tag}</span>
                    <h3 className="text-base sm:text-xl md:text-2xl font-bold text-[#294c79] mb-2 sm:mb-3 leading-tight safe-wrap">{step.title}</h3>
                    <p className="step-description mb-3 sm:mb-4">{step.description}</p>

                    {/* Verification fee callout — Step 02 only */}
                    {i === 1 && (
                      <div className="mb-3 sm:mb-4 bg-[#294c79]/5 border border-[#294c79]/15 rounded-xl sm:rounded-2xl px-3 sm:px-5 py-3 sm:py-4 space-y-2">
                        <p className="text-[#294c79] text-[9px] sm:text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2">Verification Fee</p>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="text-[#a5c250] font-bold text-xs sm:text-sm">₦10,000</span>
                          <span className="text-[#1a1a1a]/50 text-xs sm:text-sm">— for annual rents below ₦1,000,000</span>
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="text-[#a5c250] font-bold text-xs sm:text-sm">₦20,000</span>
                          <span className="text-[#1a1a1a]/50 text-xs sm:text-sm">— for annual rents of ₦1,000,000 and above</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-2 bg-[#e8f2d1] rounded-xl px-3 sm:px-4 py-2.5 sm:py-3">
                      <span className="text-[#a5c250] text-sm sm:text-lg flex-shrink-0 mt-0.5 sm:mt-0 leading-snug">✓</span>
                      <p className="text-[#4a7a1e] text-xs sm:text-sm font-medium leading-snug safe-wrap">{step.highlight}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ COST BREAKDOWN ══ */}
      <section className="bg-[#294c79] py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30"
          style={{ background: 'repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(165,194,80,0.07) 12px, rgba(165,194,80,0.07) 14px)' }} />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block border border-[#a5c250]/40 text-[#a5c250] text-[10px] sm:text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full tracking-widest uppercase mb-3 sm:mb-4">
              Fee Breakdown
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-3">What you actually pay</h2>
            <p className="text-white/60 text-sm sm:text-lg">No surprises. Everything is disclosed before you sign.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
            {[
              {
                label: 'Verification Fee (one-time)',
                value: '₦10,000 – ₦20,000',
                sub: '₦10k for rents below ₦1M/yr · ₦20k for ₦1M/yr and above · Covers your physical verification visit',
                icon: '🔐',
              },
              {
                label: 'Move-In Upfront',
                value: '30% of annual rent',
                sub: 'Paid once at the start of your tenancy',
                icon: '🔑',
              },
              {
                label: 'First Year Monthly',
                value: '70% ÷ 11 months + 4.8%',
                sub: 'Spread over 11 months after move-in',
                icon: '📅',
              },
              {
                label: 'Renewal Fee (yearly)',
                value: '₦7,000',
                sub: 'Paid each time you renew your lease',
                icon: '🔄',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white/[0.08] border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-7 flex items-start gap-3 sm:gap-4">
                <div className="text-2xl sm:text-3xl flex-shrink-0 mt-0.5">{item.icon}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-white/60 text-[10px] sm:text-xs font-semibold uppercase tracking-wide mb-1 safe-wrap">{item.label}</p>
                  <p className="text-white font-bold text-base sm:text-lg md:text-xl mb-1 safe-wrap">{item.value}</p>
                  <p className="text-white/50 text-xs sm:text-sm leading-relaxed safe-wrap">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-white/40 text-xs sm:text-sm mt-6 sm:mt-8">
            Use the <Link to="/" className="text-[#a5c250] underline underline-offset-2">Rent Calculator</Link> on our homepage to see your exact figures.
          </p>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#f5f9ee]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block bg-white border border-[#a5c250]/30 text-[#4a7a1e] text-[10px] sm:text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full tracking-widest uppercase mb-3 sm:mb-4">
              FAQ
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#294c79] mb-2 sm:mb-3">Questions we hear often</h2>
            <p className="text-[#1a1a1a]/60 text-sm sm:text-lg">Everything you need to know before you start.</p>
          </div>

          <div className="space-y-2 sm:space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl sm:rounded-2xl border border-[#e8f2d1] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-5 flex items-center justify-between text-left gap-3"
                  style={{ minHeight: 56 }}
                >
                  <p className="text-sm sm:text-base md:text-lg font-semibold text-[#294c79] leading-snug safe-wrap flex-1">{faq.q}</p>
                  <span className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 ${openFaq === i ? 'bg-[#a5c250] text-white rotate-180' : 'bg-[#e8f2d1] text-[#294c79]'}`}>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-out ${openFaq === i ? 'max-h-80 pb-5' : 'max-h-0'}`}>
                  <p className="faq-answer px-4 sm:px-6 md:px-8 safe-wrap">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-[#e8f2d1]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#294c79] mb-3 sm:mb-4 leading-tight">
            {(isLoggedIn || isAdminLoggedIn) ? 'Ready to manage your account?' : 'Ready to find your home in Ibadan?'}
          </h2>
          <p className="text-[#1a1a1a]/60 text-sm sm:text-lg mb-8 sm:mb-10">
            {(isLoggedIn || isAdminLoggedIn)
              ? 'Head to your dashboard to track applications, payments, and more.'
              : 'Browse verified homes and move in with just 30% upfront.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            {(isLoggedIn || isAdminLoggedIn) ? (
              <Link to={getDashboardLink()} className="cta-btn inline-flex items-center justify-center bg-[#294c79] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#1f3a5c] shadow-md transition active:scale-95">
                Go to Dashboard
              </Link>
            ) : (
              <Link to="/browse" className="cta-btn inline-flex items-center justify-center bg-[#a5c250] hover:bg-[#8bb83a] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-md transition active:scale-95">
                Browse Homes →
              </Link>
            )}
            <Link to="/contact" className="cta-btn inline-flex items-center justify-center border-2 border-[#294c79] text-[#294c79] px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:bg-[#294c79]/5 transition active:scale-95">
              Talk to Us
            </Link>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="bg-[#fdfdfd] border-t border-[#afc09e]/30 py-10 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 mt-auto">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-12">
          <div>
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-[#294c79] mb-3 sm:mb-5">Company</h4>
            <ul className="space-y-2 sm:space-y-3 text-[#1a1a1a]/80 text-xs sm:text-sm md:text-base">
              <li><Link to="/about" className="hover:text-[#294c79] transition">About LiteRent</Link></li>
              <li><Link to="/how-it-works" className="hover:text-[#294c79] transition">How It Works</Link></li>
              <li><Link to="/contact" className="hover:text-[#294c79] transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-[#294c79] mb-3 sm:mb-5">Tenants</h4>
            <ul className="space-y-2 sm:space-y-3 text-[#1a1a1a]/80 text-xs sm:text-sm md:text-base">
              <li><Link to="/browse" className="hover:text-[#294c79] transition">Browse Houses</Link></li>
              <li><Link to="/faq" className="hover:text-[#294c79] transition">FAQ</Link></li>
              <li><Link to="/waitlist" className="hover:text-[#294c79] transition">Join Waitlist</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-[#294c79] mb-3 sm:mb-5">Legal</h4>
            <ul className="space-y-2 sm:space-y-3 text-[#1a1a1a]/80 text-xs sm:text-sm md:text-base">
              <li><Link to="/terms" className="hover:text-[#294c79] transition">Terms</Link></li>
              <li><Link to="/privacy" className="hover:text-[#294c79] transition">Privacy</Link></li>
              <li><Link to="/agreements" className="hover:text-[#294c79] transition">Agreements</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-[#294c79] mb-3 sm:mb-5">Support</h4>
            <ul className="space-y-2 sm:space-y-3 text-[#1a1a1a]/80 text-xs sm:text-sm md:text-base">
              <li><Link to="/help" className="hover:text-[#294c79] transition">Help Center</Link></li>
              <li>
                <a href="https://wa.me/2349057582412" target="_blank" rel="noopener noreferrer" className="hover:text-[#294c79] transition">
                  WhatsApp Support
                </a>
              </li>
            </ul>
          </div>
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-[#294c79] mb-3 sm:mb-5">Compliance</h4>
            <ul className="space-y-2 sm:space-y-3 text-[#1a1a1a]/80 text-xs sm:text-sm md:text-base">
              <li>Operating in Nigeria</li>
              <li>Payments by Paystack</li>
            </ul>
          </div>
        </div>
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#afc09e]/30 text-center text-[#1a1a1a]/60 text-xs sm:text-sm">
          © {new Date().getFullYear()} LiteRent. All rights reserved.
        </div>
      </footer>

    </main>
      </>
  );
}